import { isEmpty } from "lodash";

import { AllRowsSelector, LoadingIndicator, RenderWhen } from "@components";
import { InfiniteScrollGrid } from "@components/grid";
import { useSelectRowsContext } from "@js2/hooks";
import NetworkThreatCard from "@modules/network/components/cards/NetworkThreatCard";
import NetworkThreatNoDataMsg from "@modules/network/components/common/NetworkThreatNoDataMsg";
import type { Schema } from "@modules/samples/@types";
import { createSelectedRowKey } from "@modules/samples/util";

type Props<T> = {
  data: Array<T>;
  schema: Schema<T>;
  hasMore?: boolean;
  onNextPage: (nextPageId: string, pageSize: number) => void;
  isLoading: boolean;
  isFetchingNextPage: boolean;
};

const FilesTable = <T,>({
  data,
  schema,
  onNextPage,
  hasMore,
  isLoading,
  isFetchingNextPage
}: Props<T>) => {
  const { api, selected, isSelectAll } = useSelectRowsContext();

  const handleSelectRowChange = (item: T, index: number) => {
    api.select(item, index);
  };

  const handleSelectPage = () => {
    api.selectPage(data);
  };

  const handleCheckIsSelected = (item: T, index: number) =>
    api.checkIsSelected(item, index);

  const handleSelectAll = () => {
    api.selectAll(data);
  };

  const handleDeselectAll = () => {
    api.clear();
  };

  return (
    <div>
      <RenderWhen condition={!isEmpty(selected)}>
        <AllRowsSelector
          allRowsSelected={isSelectAll}
          totalResults={data.length}
          selected={Object.values(selected)}
          onSelectAllRowsChange={handleSelectAll}
          onDeSelectAllRowsChange={handleDeselectAll}
        />
      </RenderWhen>
      <InfiniteScrollGrid
        schema={schema}
        data={data}
        showLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        selectableRows
        allRowsSelected={api.isPageSelected(data)}
        selectedRows={selected}
        onRowSelectChange={handleSelectRowChange}
        onSelectAllRowsChange={handleSelectPage}
        checkRowSelected={handleCheckIsSelected}
        createSelectedRowKey={createSelectedRowKey}
        hasMore={Boolean(hasMore)}
        onLoadMore={onNextPage}
        maxHeight={625}
        headerCellClassName="bold"
        LoadingComponent={<LoadingIndicator style={{ margin: "10rem 0" }} />}
        emptyGridMsg={
          <NetworkThreatCard.Error className="m-y-20">
            <NetworkThreatNoDataMsg />
          </NetworkThreatCard.Error>
        }
      />
    </div>
  );
};

export default FilesTable;
